import { Footer } from "@common/SimpleDrawer/components/Footer";
import { Section } from "@common/SimpleDrawer/components/Section";
import { FormAutocomplete } from "@components/FormAutocomplete/FormAutoComplete";
import { FormInput } from "@components/FormInput/FormInput";
import { FORM_ACTIONS } from "@models/FormActions";
import { useProducts } from "@features/products/queries/useProducts";
import { useLocations } from "@features/locations/queries/useLocations";
import { useEffect, useReducer, useState } from "react";
import { CreateProductDrawer } from "./CreateProductDrawer";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { useDrawer } from "@hooks/useDrawer";
import { useAuth } from "@Auth/useAuth";
import { onInfoShow } from "@utils/onInfoShow";
import { useStockStore } from "@stores/stockStore";
import { StockClient } from "@Clients/StockClient";
import { Stock } from "@models/Stock";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { returnErrorMessage } from "@utils/returnErrorMessage";
import { Entity } from "@models/Entity";
import { onErrorShow } from "@utils/onErrorShow";
import { CreateLocationDrawer } from "./CreateLocationDrawer";
import { Box, Button, Dialog, Typography } from "@mui/material";
import { useProductStagesStore } from "@stores/productStagesStore";
import { BackorderQueueClient } from "@Clients/BackorderQueueClient";

type StockInitialState = {
  product: string;
  location: string;
  quantity: number;
  current_status_id: string | null;
  errors: Record<keyof StockInitialState, string>;
};

const initialState: StockInitialState = {
  product: "",
  location: "",
  quantity: 0,
  current_status_id: null,
  errors: {} as Record<keyof StockInitialState, string>,
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: string | number;
  errors?: Record<keyof T, string>;
}

function formReducer(
  state: StockInitialState,
  action: Action<StockInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD:
      return { ...state, [action.field!]: action.value };
    case FORM_ACTIONS.SET_ERRORS:
      return { ...state, errors: action.errors };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      return state;
  }
}

type StockFormProps = {
  handleClose: () => void;
};

export function StockForm({ handleClose }: StockFormProps) {
  const {
    context,
    selectedId,
    prefillProductId,
    prefillLocationId,
    close: closeOnSubmit,
  } = useDrawer();
  const { setTouch } = useConfirmationModalStore();
  const { stages } = useProductStagesStore();
  const [openProductCreate, setOpenProductCreate] = useState(false);
  const [openLocationCreate, setOpenLocationCreate] = useState(false);
  // Set when a create would collide with an existing entry at the same
  // product+location — drives the "combine quantities?" confirm.
  const [pendingMerge, setPendingMerge] = useState<{
    existing: Stock;
    addQty: number;
  } | null>(null);

  const { data: products = [] } = useProducts();
  const { getStocksByOrganizationId, stocks, createStock, updateStock } =
    useStockStore();
  const { baseUser } = useAuth();
  const { data: locations = [] } = useLocations();

  // Helper function to get backorder fulfillment count
  const getBackorderFulfillmentCount = async (
    productId: string
  ): Promise<number> => {
    try {
      const pendingBackorders =
        await BackorderQueueClient.getBackorderQueueByProductId(productId);
      return pendingBackorders.length;
    } catch (error) {
      console.error("Error getting backorder count:", error);
      return 0;
    }
  };

  useEffect(() => {
    if (prefillProductId && context === DRAWER_CONTEXT.CREATE_STOCK) {
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "product",
        value: prefillProductId,
      });
    }
  }, [prefillProductId, context]);

  useEffect(() => {
    if (prefillLocationId && context === DRAWER_CONTEXT.CREATE_STOCK) {
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "location",
        value: prefillLocationId,
      });
    }
  }, [prefillLocationId, context]);

  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<StockInitialState>,
      Action<StockInitialState>
    >,
    initialState
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: e.target.name as keyof StockInitialState,
      value: e.target.value,
    });
    setTouch(true);
  };

  useEffect(() => {
    if (context !== DRAWER_CONTEXT.EDIT_STOCK || !selectedId) return;

    const populate = (stock: Stock) => {
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "product",
        value: stock.product_id,
      });
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "location",
        value: stock.location_id,
      });
      dispatch({
        type: FORM_ACTIONS.SET_FIELD,
        field: "quantity",
        value: stock.quantity,
      });
      if (stock.current_status_id) {
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "current_status_id",
          value: stock.current_status_id,
        });
      }
    };

    const fromStore = stocks.find((stock) => stock.id === selectedId);
    if (fromStore) {
      populate(fromStore);
      return;
    }

    // The org-wide store isn't always loaded (e.g. the inventory hub reads
    // stock per-location via TanStack and never populates it), so fetch the
    // single row directly rather than rendering an empty form.
    let active = true;
    StockClient.getStockById(selectedId)
      .then((stock) => {
        if (active && stock) populate(stock);
      })
      .catch(() => {
        /* surfaced elsewhere; leave the form empty rather than crash */
      });
    return () => {
      active = false;
    };
  }, [context, selectedId, stocks]);

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      const errors = {} as Record<keyof StockInitialState, string>;

      const { product, location, quantity } = state;
      const requiredFields: (keyof StockInitialState)[] = [
        "product",
        "location",
        "quantity",
      ];

      requiredFields.forEach((field) => {
        if (!state[field]) errors[field] = `${field} is required`;
      });

      if (Object.keys(errors).length > 0) {
        dispatch({ type: FORM_ACTIONS.SET_ERRORS, errors });
        return;
      }

      if (context === DRAWER_CONTEXT.EDIT_STOCK && selectedId) {
        // Get backorder count before processing
        const backorderCount = await getBackorderFulfillmentCount(product!);

        // Use store method which includes backorder processing
        await updateStock({
          id: selectedId,
          product_id: product!,
          location_id: location!,
          quantity: quantity!,
          organization_id: baseUser!.organization_id!,
          current_status_id: state.current_status_id || null,
          committed_quantity: 0, // Default to 0, will be updated by backorder processing
        });

        closeOnSubmit();

        // Show enhanced success message with backorder info
        const successMessage =
          backorderCount > 0
            ? `Stock updated successfully! ${backorderCount} backorders are being processed.`
            : "Stock updated successfully!";
        onInfoShow(successMessage);

        // Dispatch custom event to refresh backorder dashboard
        window.dispatchEvent(
          new CustomEvent("stockUpdated", {
            detail: { productId: product!, backorderCount },
          })
        );

        getStocksByOrganizationId(baseUser!.organization_id!);
        return;
      }
      // A product can only have one entry per location. If one already exists,
      // confirm a quantity merge instead of inserting a duplicate.
      const existing = await StockClient.getStockByProductAndLocation(
        product!,
        location!
      );
      if (existing) {
        setPendingMerge({ existing, addQty: Number(quantity) });
        return;
      }

      // Get backorder count before processing
      const backorderCount = await getBackorderFulfillmentCount(product!);

      // Use store method which includes backorder processing
      await createStock({
        product_id: product!,
        location_id: location!,
        quantity: quantity!,
        organization_id: baseUser!.organization_id!,
        current_status_id: state.current_status_id || null,
        committed_quantity: 0, // Default to 0, will be updated by backorder processing
      });

      closeOnSubmit();

      // Show enhanced success message with backorder info
      const successMessage =
        backorderCount > 0
          ? `Stock added successfully! ${backorderCount} backorders are being processed.`
          : "Stock added successfully!";
      onInfoShow(successMessage);

      // Dispatch custom event to refresh backorder dashboard
      window.dispatchEvent(
        new CustomEvent("stockUpdated", {
          detail: { productId: product!, backorderCount },
        })
      );

      getStocksByOrganizationId(baseUser!.organization_id!);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      //TODO: HANDLE ERROR RELATED TO THE WHOLE TABLE
      // LIKE NO PERMISSIONS TO INSERT
      if (error.message) {
        const errorMessage = returnErrorMessage(Entity.STOCK, error.message);
        onErrorShow(errorMessage || "Failed to save stock");
        return;
      }
      onErrorShow("Failed to save stock");
    }
  };

  const confirmMerge = async () => {
    if (!pendingMerge) return;
    const { existing, addQty } = pendingMerge;
    try {
      const backorderCount = await getBackorderFulfillmentCount(
        existing.product_id
      );
      // Reuse the update path (it runs backorder processing + invalidation).
      await updateStock({
        id: existing.id,
        product_id: existing.product_id,
        location_id: existing.location_id,
        organization_id: existing.organization_id,
        quantity: existing.quantity + addQty,
        committed_quantity: existing.committed_quantity,
        current_status_id: existing.current_status_id,
      });
      setPendingMerge(null);
      closeOnSubmit();
      onInfoShow(
        backorderCount > 0
          ? `Combined into the existing entry. ${backorderCount} backorders are being processed.`
          : "Combined into the existing entry."
      );
      window.dispatchEvent(
        new CustomEvent("stockUpdated", {
          detail: { productId: existing.product_id, backorderCount },
        })
      );
      getStocksByOrganizationId(baseUser!.organization_id!);
    } catch {
      onErrorShow("Failed to combine stock");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormAutocomplete
            label="Product"
            disabled={
              context === DRAWER_CONTEXT.EDIT_STOCK || !!prefillProductId
            }
            value={state.product}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "product" as keyof StockInitialState,
                value: value,
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select a product..."
            errorMessage={state.errors?.product}
            actionButtonText="New Product"
            options={products.map((p) => ({
              label: p.name,
              value: p.id,
            }))}
            actionButtonOnClick={() => {
              setOpenProductCreate(true);
            }}
          />
          <FormAutocomplete
            label="Location"
            value={state.location}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "location" as keyof StockInitialState,
                value: value,
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select a location..."
            errorMessage={state.errors?.location}
            actionButtonText="New Location"
            options={locations.map((p) => ({
              label: `${p.path?.map((p) => p.name).join(" / ")}`,
              value: p.id,
            }))}
            actionButtonOnClick={() => {
              setOpenLocationCreate(true);
            }}
          />

          <FormInput
            name="quantity"
            label="Quantity"
            value={state.quantity}
            onChange={handleChange}
            errorMessage={state.errors?.quantity}
          />
          <FormAutocomplete
            label="Status"
            value={state.current_status_id || ""}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "current_status_id" as keyof StockInitialState,
                value: value,
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select a status..."
            errorMessage={state.errors?.current_status_id}
            actionButtonText="New Status"
            options={stages.map((p) => ({
              label: p.name,
              value: p.id,
            }))}
            actionButtonOnClick={() => {
              setOpenLocationCreate(true);
            }}
          />
        </Section>
      </Box>

      <Footer handleClose={handleClose} handleSubmit={handleSubmit} />
      <CreateProductDrawer
        onClose={() => setOpenProductCreate(false)}
        open={openProductCreate}
      />
      <CreateLocationDrawer
        onClose={() => setOpenLocationCreate(false)}
        open={openLocationCreate}
      />

      <Dialog open={!!pendingMerge} onClose={() => setPendingMerge(null)}>
        <Box sx={{ p: 3, maxWidth: 420 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Combine with existing entry?
          </Typography>
          {pendingMerge && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This location already has{" "}
              <strong>{pendingMerge.existing.quantity}</strong> of this item.
              Adding here combines them into{" "}
              <strong>
                {pendingMerge.existing.quantity + pendingMerge.addQty}
              </strong>
              .
            </Typography>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => setPendingMerge(null)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={confirmMerge}>
              Combine
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
